<script setup lang="ts">
import { fileIcon, formatFileSize, formatUploadDate } from '@/components/documents/fileMeta'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import { highlightParts } from '@/pages/investors/documents/highlight'
import { useDocumentAreas } from '@/pages/investors/documents/useDocumentAreas'
import AttachmentController from '@controllers/AttachmentController'
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DocumentRowData = App.Data.Documents.DocumentRowData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import DocumentUploadAreaData = App.Data.Documents.DocumentUploadAreaData

const props = defineProps<{
  rows: DocumentRowData[]
  uploadAreas: DocumentUploadAreaData[]
}>()

const { t, locale } = useI18n()
const { areaLabel, areaIcon } = useDocumentAreas()
const { classify } = useDocumentUpload()
const confirm = useConfirm()

type SortColumn = 'document' | 'filename' | 'area' | 'target'

const PAGE_SIZE = 15

const search = ref('')
const sortColumn = ref<SortColumn>('document')
const sortAscending = ref(true)
const page = ref(1)
const showAll = ref(false)
const editVisible = ref(false)
const editDocument = ref<DocumentRowData | null>(null)
const editFilename = ref('')
const editDescription = ref<string | null>(null)

const searchable = (row: DocumentRowData): string =>
  [row.documentLabel ?? t('unassigned'), row.attachment.filename ?? '', row.targetLabel, row.targetSubLabel ?? '']
    .join(' ')
    .toLowerCase()

const filtered = computed(() => {
  const needle = search.value.trim().toLowerCase()

  return props.rows.filter((row) => !needle || searchable(row).includes(needle))
})

const sortValue = (row: DocumentRowData): string => {
  switch (sortColumn.value) {
    case 'filename':
      return row.attachment.filename ?? ''
    case 'area':
      return areaLabel(row.area)
    case 'target':
      return row.targetLabel
    default:
      return row.documentLabel ?? t('unassigned')
  }
}

const sorted = computed(() =>
  [...filtered.value].sort((left, right) => {
    const comparison = sortValue(left).localeCompare(sortValue(right), locale.value)

    return sortAscending.value ? comparison : -comparison
  }),
)

const pageCount = computed(() => Math.max(1, Math.ceil(sorted.value.length / PAGE_SIZE)))

const visible = computed(() =>
  showAll.value ? sorted.value : sorted.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE),
)

watch([filtered, showAll], () => {
  page.value = 1
})

function toggleSort(column: SortColumn): void {
  if (sortColumn.value === column) {
    sortAscending.value = !sortAscending.value

    return
  }

  sortColumn.value = column
  sortAscending.value = true
}

function typeOptions(attachableType: string): DocumentTypeOptionData[] {
  return props.uploadAreas.find((area) => area.attachableType === attachableType)?.typeOptions ?? []
}

function downloadUrl(row: DocumentRowData): string | undefined {
  return row.attachment.id ? AttachmentController.download(row.attachment.id).url : undefined
}

function fileMeta(row: DocumentRowData): string {
  const parts = [
    row.fileTotal ? t('fileOfFiles', { position: row.filePosition, total: row.fileTotal }) : undefined,
    formatFileSize(row.attachment.size, locale.value),
    formatUploadDate(row.attachment.updated_at, locale.value),
  ]

  return parts.filter(Boolean).join(' · ')
}

function resetSearch(): void {
  search.value = ''
}

function openEdit(row: DocumentRowData): void {
  editDocument.value = row
  editFilename.value = row.attachment.filename ?? ''
  editDescription.value = row.attachment.description ?? null
  editVisible.value = true
}

function resetEdit(): void {
  editDocument.value = null
  editFilename.value = ''
  editDescription.value = null
}

function saveEdit(): void {
  const attachmentId = editDocument.value?.attachment.id

  if (!attachmentId) {
    return
  }

  router.patch(
    AttachmentController.update(attachmentId).url,
    { filename: editFilename.value, description: editDescription.value },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => (editVisible.value = false),
    },
  )
}

function confirmDelete(row: DocumentRowData): void {
  const attachmentId = row.attachment.id

  if (!attachmentId) {
    return
  }

  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { filename: row.attachment.filename }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => router.delete(AttachmentController.destroy(attachmentId).url, { preserveScroll: true }),
  })
}
</script>

<template>
  <FinestCard :title="t('cardTitle')" icon="pi pi-folder-open" flush>
    <div class="mb-4 flex flex-wrap items-center gap-3 px-5">
      <div class="relative min-w-60 flex-1">
        <i class="pi pi-search text-ink-3 absolute top-1/2 left-3 -translate-y-1/2 text-sm" aria-hidden="true" />
        <input
          v-model="search"
          type="search"
          class="finest-field pr-9 pl-9"
          :placeholder="t('searchPlaceholder')"
          :aria-label="t('searchPlaceholder')" />
        <button
          v-if="search"
          type="button"
          class="bg-surface-3 text-ink-3 hover:text-ink absolute top-1/2 right-2.5 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full"
          :aria-label="t('resetSearch')"
          @click="resetSearch">
          <i class="pi pi-times text-[10px]!" aria-hidden="true" />
        </button>
      </div>
    </div>

    <p v-if="search" class="text-ink-3 mb-3 px-5 text-sm">
      {{ t('hits', { count: filtered.length, query: search }) }} ·
      <button type="button" class="text-action cursor-pointer hover:underline" @click="resetSearch">
        {{ t('resetSearch') }}
      </button>
    </p>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[900px] border-collapse text-base">
        <thead>
          <tr>
            <th
              v-for="column in ['document', 'filename', 'area', 'target'] as SortColumn[]"
              :key="column"
              scope="col"
              class="border-line cursor-pointer border-b px-3 py-2 text-left text-xs font-semibold tracking-wider uppercase first:pl-5"
              :class="sortColumn === column ? 'text-action' : 'text-ink-3'"
              :aria-sort="sortColumn === column ? (sortAscending ? 'ascending' : 'descending') : 'none'"
              @click="toggleSort(column)">
              {{ t(`columns.${column}`) }}
              <i
                class="pi ml-1 text-[9px]!"
                :class="
                  sortColumn === column ? (sortAscending ? 'pi-sort-up-fill' : 'pi-sort-down-fill') : 'pi-sort-alt'
                "
                aria-hidden="true" />
            </th>
            <th
              scope="col"
              class="border-line text-ink-3 border-b px-3 py-2 pr-5 text-right text-xs font-semibold tracking-wider uppercase">
              {{ t('columns.action') }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in visible" :key="row.key" class="border-line-2 hover:bg-surface-2 h-20 border-b">
            <td class="px-3 py-2.5 pl-5 align-middle">
              <FinestSelect
                v-if="!row.documentLabel"
                :model-value="null"
                :options="typeOptions(row.attachableType)"
                :placeholder="t('unassigned')"
                missing
                :aria-label="t('assignType')"
                class="max-w-56"
                @change="(value) => value && row.attachment.id && classify(row.attachment.id, value)" />

              <span v-else class="font-medium">{{ row.documentLabel }}</span>
            </td>

            <td class="px-3 py-2.5 align-middle">
              <div class="flex min-w-0 items-center gap-2">
                <i :class="fileIcon(row.attachment)" class="text-ink-3 flex-none text-xs!" aria-hidden="true" />
                <a :href="downloadUrl(row)" class="text-action min-w-0 truncate font-medium hover:underline">
                  <template v-for="(part, index) in highlightParts(row.attachment.filename ?? '', search)">
                    <mark v-if="part.match" :key="index" class="rounded bg-[#fff3c4] text-inherit">
                      {{ part.text }}
                    </mark>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </a>
              </div>
              <span class="text-ink-3 block text-sm">{{ fileMeta(row) }}</span>
            </td>

            <td class="px-3 py-2.5 align-middle">
              <i
                :class="areaIcon(row.area)"
                class="text-ink-2 text-xl!"
                :title="areaLabel(row.area)"
                aria-hidden="true" />
              <span class="sr-only">{{ areaLabel(row.area) }}</span>
            </td>

            <td class="text-ink-2 px-3 py-2.5 align-middle">
              <span class="block">{{ row.targetLabel }}</span>
              <span v-if="row.targetSubLabel" class="text-ink-3 block text-sm">{{ row.targetSubLabel }}</span>
            </td>

            <td class="px-3 py-2.5 pr-5 text-right align-middle">
              <div class="flex justify-end gap-2">
                <a
                  :href="downloadUrl(row)"
                  class="border-line text-ink-2 hover:border-action hover:bg-info-subtle hover:text-action inline-flex size-8 items-center justify-center rounded-md border transition-colors"
                  :title="t('download')"
                  :aria-label="t('download')">
                  <i class="pi pi-download text-xs!" aria-hidden="true" />
                </a>
                <button
                  type="button"
                  class="border-line text-ink-2 hover:border-action hover:bg-info-subtle hover:text-action inline-flex size-8 cursor-pointer items-center justify-center rounded-md border transition-colors"
                  :title="t('edit')"
                  :aria-label="t('edit')"
                  @click="openEdit(row)">
                  <i class="pi pi-pencil text-xs!" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="border-line text-ink-2 hover:border-danger hover:bg-danger-subtle hover:text-danger inline-flex size-8 cursor-pointer items-center justify-center rounded-md border transition-colors"
                  :title="t('delete.action')"
                  :aria-label="t('delete.action')"
                  @click="confirmDelete(row)">
                  <i class="pi pi-trash text-xs!" aria-hidden="true" />
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!visible.length">
            <td colspan="5" class="text-ink-3 px-3 py-10 text-center">{{ t('empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-3 flex flex-wrap items-center justify-between gap-3 px-5 pb-5">
      <p class="text-ink-3 text-sm">
        {{ t('entries', { shown: visible.length, total: sorted.length }) }}
        <button
          v-if="!showAll && sorted.length > PAGE_SIZE"
          type="button"
          class="text-action cursor-pointer hover:underline"
          @click="showAll = true">
          {{ t('showAll') }}
        </button>
      </p>

      <div v-if="!showAll && pageCount > 1" class="flex items-center gap-3">
        <FinestButton
          variant="ghost"
          size="sm"
          :label="t('previous')"
          :disabled="page === 1"
          @click="page = page - 1" />
        <span class="text-ink-3 text-sm">{{ t('pageOf', { page, pageCount }) }}</span>
        <FinestButton
          variant="ghost"
          size="sm"
          :label="t('next')"
          :disabled="page === pageCount"
          @click="page = page + 1" />
      </div>
    </div>
  </FinestCard>

  <Dialog
    v-model:visible="editVisible"
    modal
    :header="t('editDialog.title')"
    class="w-full max-w-lg"
    @after-hide="resetEdit">
    <form class="flex flex-col gap-4" @submit.prevent="saveEdit">
      <label class="flex flex-col gap-1.5">
        <span class="text-ink-2 text-sm font-medium">{{ t('editDialog.filename') }}</span>
        <InputText v-model="editFilename" :aria-label="t('editDialog.filename')" maxlength="255" required fluid />
      </label>

      <label class="flex flex-col gap-1.5">
        <span class="text-ink-2 text-sm font-medium">{{ t('editDialog.documentType') }}</span>
        <FinestSelect
          v-model="editDescription"
          :options="editDocument ? typeOptions(editDocument.attachableType) : []"
          :placeholder="t('unassigned')"
          :aria-label="t('editDialog.documentType')" />
      </label>

      <div class="flex justify-end gap-2">
        <FinestButton :label="t('editDialog.cancel')" variant="ghost" @click="editVisible = false" />
        <FinestButton type="submit" :label="t('editDialog.save')" icon="pi pi-check" />
      </div>
    </form>
  </Dialog>
</template>

<i18n lang="json">
{
  "de": {
    "cardTitle": "Dokumentenübersicht",
    "searchPlaceholder": "Nach Dateiname, Dokumentart oder Objekt suchen",
    "resetSearch": "Suche zurücksetzen",
    "hits": "{count} Treffer für „{query}“",
    "columns": {
      "document": "Dokument",
      "filename": "Dateiname",
      "area": "Bereich",
      "target": "Objekt",
      "action": "Aktion"
    },
    "unassigned": "Nicht zugeordnet",
    "assignType": "Dokumentart zuordnen",
    "fileOfFiles": "{position} von {total} Dateien",
    "download": "Herunterladen",
    "edit": "Dokument bearbeiten",
    "editDialog": {
      "title": "Dokument bearbeiten",
      "filename": "Dateiname",
      "documentType": "Dokumentart",
      "cancel": "Abbrechen",
      "save": "Speichern"
    },
    "delete": {
      "action": "Dokument entfernen",
      "header": "Dokument entfernen",
      "message": "„{filename}“ wird entfernt.",
      "reject": "Abbrechen",
      "accept": "Entfernen"
    },
    "empty": "Keine Einträge für diese Auswahl.",
    "entries": "{shown} von {total} Einträgen",
    "showAll": "alle anzeigen",
    "previous": "Zurück",
    "next": "Weiter",
    "pageOf": "Seite {page} von {pageCount}"
  },
  "en": {
    "cardTitle": "Document overview",
    "searchPlaceholder": "Search by filename, document type or object",
    "resetSearch": "Reset search",
    "hits": "{count} hits for “{query}”",
    "columns": {
      "document": "Document",
      "filename": "Filename",
      "area": "Area",
      "target": "Object",
      "action": "Action"
    },
    "unassigned": "Not assigned",
    "assignType": "Assign document type",
    "fileOfFiles": "{position} of {total} files",
    "download": "Download",
    "edit": "Edit document",
    "editDialog": {
      "title": "Edit document",
      "filename": "Filename",
      "documentType": "Document type",
      "cancel": "Cancel",
      "save": "Save"
    },
    "delete": {
      "action": "Remove document",
      "header": "Remove document",
      "message": "“{filename}” will be removed.",
      "reject": "Cancel",
      "accept": "Remove"
    },
    "empty": "No entries for this selection.",
    "entries": "{shown} of {total} entries",
    "showAll": "show all",
    "previous": "Previous",
    "next": "Next",
    "pageOf": "Page {page} of {pageCount}"
  }
}
</i18n>
